import { usePage } from '@inertiajs/react';
import { useEffect, useState } from 'react';

const ICONS = {
    success: (
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    ),
    error: (
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
    ),
    warning: (
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    ),
    info: (
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
    ),
};

const TONES = {
    success: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200',
    error: 'border-rose-500/30 bg-rose-500/10 text-rose-200',
    warning: 'border-amber-400/30 bg-amber-400/10 text-amber-200',
    info: 'border-sky-500/30 bg-sky-500/10 text-sky-200',
};

const ICON_TONES = {
    success: 'text-emerald-300',
    error: 'text-rose-300',
    warning: 'text-amber-300',
    info: 'text-sky-300',
};

function Toast({ item, onDismiss }) {
    useEffect(() => {
        const timer = setTimeout(() => onDismiss(item.id), 6000);
        return () => clearTimeout(timer);
    }, [item.id, onDismiss]);

    return (
        <div
            role="alert"
            className={`pointer-events-auto flex w-full items-start gap-3 rounded-xl border p-4 shadow-card backdrop-blur animate-scale-in ${TONES[item.type]}`}
        >
            <svg className={`mt-0.5 h-5 w-5 shrink-0 ${ICON_TONES[item.type]}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                {ICONS[item.type]}
            </svg>
            <p className="flex-1 text-sm font-medium leading-6">{item.message}</p>
            <button
                type="button"
                onClick={() => onDismiss(item.id)}
                className="shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-white/5 hover:text-slate-100"
                aria-label="بستن"
            >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    );
}

export default function FlashMessages() {
    const { flash, errors } = usePage().props;
    const [items, setItems] = useState([]);

    useEffect(() => {
        const next = [];
        if (flash?.success) next.push({ type: 'success', message: flash.success });
        if (flash?.error) next.push({ type: 'error', message: flash.error });
        if (flash?.warning) next.push({ type: 'warning', message: flash.warning });
        if (flash?.info) next.push({ type: 'info', message: flash.info });

        // Surface validation errors as toasts too.
        const validationMessages = errors ? Object.values(errors) : [];
        validationMessages.forEach((message) => {
            if (message) next.push({ type: 'error', message });
        });

        if (next.length === 0) return;

        setItems((prev) => [
            ...prev,
            ...next.map((n, i) => ({ ...n, id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2)}` })),
        ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flash?.success, flash?.error, flash?.warning, flash?.info, errors]);

    const dismiss = (id) => setItems((prev) => prev.filter((item) => item.id !== id));

    if (items.length === 0) return null;

    return (
        <div className="pointer-events-none fixed inset-x-0 top-4 z-[100] flex flex-col items-center gap-2 px-4 sm:inset-x-auto sm:right-4 sm:items-end">
            <div className="flex w-full max-w-sm flex-col gap-2">
                {items.map((item) => (
                    <Toast key={item.id} item={item} onDismiss={dismiss} />
                ))}
            </div>
        </div>
    );
}
